import React, { useState, useEffect } from "react";
import "../../SpearParts/Layouts/Layouts.css";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  getCompleteData,
  getItems,
  getYears,
  searchPart,
} from "../../../pages/Engineering/Home/EngineeringHome";
import Axios from "../../../axios/axios";
import { toast } from "react-toastify";
import Spinner from "../../Loader/Spinner";

const EngMiddle = ({
  initialFilters = {},
  engineeringBrand: initialBrand = [],
  engineeringCategory: initialCategory = [],
  engineeringModels: initialModels = [],
  region: initialRegion = [],
  years: initialYears = [],
}) => {
  const [engineeringModels, setEngineeringModels] = useState(initialModels);
  const [engineeringBrand, setEngineeringBrand] = useState(initialBrand);
  const [engineeringCategory, setEngineeringCategory] =
    useState(initialCategory);
  const [region, setRegion] = useState(initialRegion);
  const [years, setYears] = useState(initialYears);
  const [manufacturers, setManufacturers] = useState([]);

  const [loading, setLoading] = useState(false);

  // controlled select states
  const [selectedCategory, setSelectedCategory] = useState(
    initialFilters.categoryId || ""
  );
  const [selectedBrand, setSelectedBrand] = useState(
    initialFilters.brandId || ""
  );
  const [selectedManufacturer, setSelectedManufacturer] = useState(
    initialFilters.manufacturerId || ""
  );

  const navigate = useNavigate();

  // Filter brands based on selected category
  const filteredBrands = selectedCategory
    ? engineeringBrand.filter((b) => String(b.categoryId?._id || b.categoryId) === String(selectedCategory))
    : engineeringBrand;

  useEffect(() => {
    getCompleteData(
      setEngineeringModels,
      setEngineeringBrand,
      setEngineeringCategory
    );
    fetchManufacturers();
    getItems(setRegion);
    setYears(getYears());

    if (initialFilters.brandId) {
      getModelData(initialFilters.brandId);
    }
  }, []);

  const fetchManufacturers = async () => {
    try {
      const res = await Axios.get("/manufacturer");
      setManufacturers(res.data);
    } catch (err) {
      console.error("Error fetching manufacturers:", err);
    }
  };

  const getModelData = async (brandId = selectedBrand) => {
    if (!brandId) {
      setEngineeringModels([]);
      return;
    }
    try {
      const res = await Axios.post("/model/modelBybrand", { brand: brandId });
      setEngineeringModels(res.data);
    } catch (error) {
      console.error("Error fetching models:", error);
      setEngineeringModels([]);
    }
  };

  const handleSearch = async () => {
    const filters = {
      ...initialFilters,
      categoryId: selectedCategory || initialFilters.categoryId || "",
      brandId: selectedBrand || initialFilters.brandId || "",
      manufacturerId:
        selectedManufacturer || initialFilters.manufacturerId || "",
    };

    if (Object.keys(filters).length === 0) {
      toast.warning("Please select at least one filter.", {
        position: "bottom-center",
        autoClose: 5000,
      });
      return;
    }

    setLoading(true);

    try {
      const fetchedParts = await searchPart(filters);
      const searchParams = new URLSearchParams(filters).toString();

      if (filters.categoryId) {
        const selectedCatObj = engineeringCategory.find(
          (cat) => cat._id === filters.categoryId
        );

        if (
          selectedCatObj &&
          selectedCatObj.subcategories &&
          selectedCatObj.subcategories.length > 0
        ) {
          // Category has subcategories → go to Subcategory page
          navigate(`/engineering/subcategories`, {
            state: {
              category: selectedCatObj,
              subcategories: selectedCatObj.subcategories,
              selectedFilters: filters,
            },
          });
        } else {
          // No subcategories → go to Product List page
          navigate(`/engineering/productlist?${searchParams}`, {
            state: {
              parts: fetchedParts,
              engineeringBrand,
              engineeringModels,
              engineeringCategory,
              region,
              years,
              selectedFilters: filters,
            },
          });
        }
      } else {
        // No category selected → go to Category page
        navigate(`/engineering/category?${searchParams}`, {
          state: {
            engineeringBrand,
            engineeringModels,
            engineeringCategory,
            region,
            years,
            selectedFilters: filters,
          },
        });
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching parts:", error);
      toast.error(
        "❌ An error occurred while fetching parts. Please try again."
      );
    }
  };


  const hasSelectedFilters = !!(selectedCategory || selectedBrand || selectedManufacturer);

  const handleClearFilters = () => {
    setSelectedCategory("");
    setSelectedBrand("");
    setSelectedManufacturer("");
    setEngineeringModels([]);
  };

  const sectionStyle = {
    backgroundImage: "url('images/slide1.jpg')",
    backgroundSize: "cover",
    backgroundPosition: "center",
  };

  const style = {
    fontFamily: "'Poppins', sans-serif",
    position: "relative",
    fontWeight: 700,
    color: "#353C4B",
  };

  const customStyles = {
    control: (base, state) => ({
      ...base,
      height: 50,
      minHeight: 50,
      borderRadius: 6,
      borderColor: "#ccc",
      backgroundColor: "#fff",
      paddingLeft: 4,
      fontFamily: "Poppins, sans-serif",
      fontSize: 15,
      boxShadow: state.isFocused ? "0 0 0 2px rgba(30,99,216,0.2)" : "none",
      "&:hover": { borderColor: "#ccc" },
    }),

    menu: (base) => ({
      ...base,
      marginTop: 2,
      borderRadius: 6,
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      zIndex: 99999,
      backgroundColor: "#fff",
      position: "absolute",
    }),

    menuList: (base) => ({
      ...base,
      paddingTop: 0,
      paddingBottom: 0,
      maxHeight: 200,
    }),

    option: (base, state) => ({
      ...base,
      padding: "10px 12px",
      fontSize: 15,
      backgroundColor: state.isSelected
        ? "#efefef"
        : state.isFocused
          ? "#f3f4f6"
          : "#ffffff",
      color: "#374151",
      cursor: "pointer",
    }),

    placeholder: (base) => ({
      ...base,
      color: "#374151",
    }),

    dropdownIndicator: (base) => ({
      ...base,
      color: "#6b7280",
      paddingRight: 12,
    }),

    indicatorSeparator: () => ({
      display: "none",
    }),

    valueContainer: (base) => ({
      ...base,
      paddingLeft: 4,
    }),

    // This is crucial for proper z-index handling
    menuPortal: (base) => ({
      ...base,
      zIndex: 99999,
    }),
  };

  return (
    <div className="Middle-Container">
      <div className="Middle-Content">
        {/* Heading Section */}
        <div className="Middle-Heading-Section">
          <h1 className="Middle-Title">Find Marine and Engineering Parts</h1>
          <p className="Middle-Description">
            Whether you're a professional mechanic , finding the right part for
            your vehicle has never been easier. Use our precise search tool to
            locate the exact spare part you need.
          </p>
        </div>

        {/* Search Form */}
        <div className="Middle-Search-Form">
          <div className="Middle-Select-Wrapper">
            <Select
              options={engineeringCategory.map((cat) => ({
                value: cat._id,
                label: cat.name,
              }))}
              value={
                engineeringCategory
                  .map((cat) => ({ value: cat._id, label: cat.name }))
                  .find((c) => c.value === selectedCategory) || null
              }
              onChange={(selectedOption) => {
                setSelectedCategory(selectedOption ? selectedOption.value : "");
                setSelectedBrand(""); // Reset brand when category changes
                setEngineeringModels([]); // Reset models
              }}
              placeholder="Category"
              styles={customStyles}
              isSearchable isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          {/* Brand */}
          <div className="Middle-Select-Wrapper">
            <Select
              options={filteredBrands.map((brand) => ({
                value: brand._id,
                label: brand.name,
              }))}
              value={
                engineeringBrand
                  .map((brand) => ({
                    value: brand._id,
                    label: brand.name,
                  }))
                  .find((b) => b.value === selectedBrand) || null
              }
              onChange={(selectedOption) => {
                const brandId = selectedOption ? selectedOption.value : "";
                setSelectedBrand(brandId);
                getModelData(brandId);
              }}
              placeholder="Brand"
              styles={customStyles}
              isSearchable isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          {/* Manufacturer */}
          <div className="Middle-Select-Wrapper">
            <Select
              options={manufacturers.map((m) => ({
                value: m._id,
                label: m.manufacturerName,
              }))}
              value={
                manufacturers
                  .map((m) => ({
                    value: m._id,
                    label: m.manufacturerName,
                  }))
                  .find((m) => m.value === selectedManufacturer) || null
              }
              onChange={(selectedOption) =>
                setSelectedManufacturer(
                  selectedOption ? selectedOption.value : ""
                )
              }
              placeholder="Manufacturer"
              styles={customStyles}
              isSearchable isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          {/* Search Button */}
          <div className="Middle-Button-Wrapper" style={{ display: "flex", gap: "8px" }}>
            <button onClick={handleSearch} className="Middle-Search-Button">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="Middle-Search-Icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              Search
            </button>
            {hasSelectedFilters && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="Middle-Search-Button"
                style={{ backgroundColor: "#dc3545", borderColor: "#dc3545" }}
                title="Clear selected search filters"
              >
                ✕ Clear
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngMiddle;
